<template>
  <div ref="target" :class="[elevated && 'bg-light-gray px-9 py-6 lg:px-28 lg:py-20']">
    <lite-youtube
      v-if="data.provider === 'youtube'"
      :playlabel="$t('media.play')"
      :videoid="`${data.providerUid}`"
      class="aspect-video h-full w-full"
      :style="{
        backgroundImage: `url(https://i.ytimg.com/vi/${data.providerUid}/hqdefault.jpg)`
      }"
      :class="[elevated && 'rounded-3xl']"
    >
    </lite-youtube>
    <iframe
      v-else-if="data.provider === 'vimeo'"
      :src="`https://player.vimeo.com/video/${data.providerUid}`"
      width="640"
      height="360"
      frameborder="0"
      allow="autoplay; fullscreen; picture-in-picture"
      class="aspect-video"
      :class="[elevated && 'rounded-3xl']"
      allowfullscreen
    ></iframe>
  </div>
</template>

<script setup lang="ts">
import 'lite-youtube-embed/src/lite-yt-embed.css'
import type { VideoField } from '~/types/generated'

defineProps({
  data: {
    type: Object as PropType<VideoField>,
    default: () => {}
  },
  elevated: {
    type: Boolean,
    default: false
  }
})

const target = ref(null)
const isVisible = ref(false)

const { stop } = useIntersectionObserver(
  target,
  ([{ isIntersecting }], _observerElement) => {
    if (isIntersecting) {
      isVisible.value = true
    }
  },
  {
    threshold: 0.5
  }
)

onBeforeUnmount(() => {
  stop()
})
</script>

<style lang="postcss">
lite-youtube {
  @apply border-2 border-black;

  background-color: #000;
  position: relative;
  display: block;
  contain: content;
  background-position: center center;
  background-size: cover;
  cursor: pointer;
  max-width: none;
}

lite-youtube::before {
  @apply h-full w-full bg-black bg-opacity-50 p-0;
  background-image: none;
}

lite-youtube::after {
  display: none;
}

/* play button */
lite-youtube .lty-playbtn {
  width: 140px;
  height: 60px;
  position: absolute;
  cursor: pointer;
  transform: translate3d(-50%, -50%, 0);
  top: 50%;
  left: 50%;
  z-index: 1;
  background-size: 140px 60px;
  background-repeat: no-repeat;
  background-image: url('~/assets/images/ui/play.png');
  transition: filter 0.1s cubic-bezier(0, 0, 0.2, 1);
  border: none;
  filter: none;

  @screen lg {
    width: 180px;
    height: 80px;
    background-size: 180px 80px;
  }
}

/* Post-click styles */
lite-youtube.lyt-activated {
  cursor: unset;
}
lite-youtube.lyt-activated::before,
lite-youtube.lyt-activated > .lty-playbtn {
  opacity: 0;
  pointer-events: none;
}

.lyt-visually-hidden {
  @apply sr-only;
}
</style>
